import React, { useEffect, useRef } from "react"
import * as am5 from "@amcharts/amcharts5"
import * as am5percent from "@amcharts/amcharts5/percent"

import { applyCategoryOrder } from "../categoryOrder"
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated"
import * as am5plugins_exporting from "@amcharts/amcharts5/plugins/exporting"
import { useMediaQuery } from "react-responsive"
import { round } from "lodash"

import {
  filterMenuItems,
  getChartTextLabel,
  getNumberFormat,
  formatNumberDisplayed,
  transformeData,
} from "../utils/global"
import { useCleanChart } from "../utils/hooks/useCleanChart"
import { getPieChartLabelFormat } from "./pieChartUtils"
import {
  createLegend,
  clearLegends,
  createExportMenu,
  configureNumberFormatting,
  resolveMappedColor,
} from "../utils/utils"
import type { ColorMapperRow } from "../utils/utils"

// Types
interface PieChartProps {
  idChart?: string
  widgetTitle?: string
  height?: string
  width?: string
  dataChart: any[]
  dataNames?: string[]
  innerRadius?: number
  enableExportMenu?: boolean
  // TITLE
  hideTitleInCenter?: boolean
  isCustomCenterValue?: boolean
  centeredValue?: "default" | "custom" | "value_and_custom"
  customTextTitleCenter?: string
  colorTitleCenter?: string
  fontSizeTitleCenter?: number | string
  fontWeightTitleCenter?: string
  // LABEL
  displayAlignLabelType?: string
  disableLabel?: boolean
  colorLabel?: string
  positionLabel?: number
  fontSizeLabel?: number
  customLabelText?: string
  typeOfExternalLabel?: "value" | "text" | "text_and_value"
  typeLabelText?: "value" | "percent"
  numberDecimal?: number
  decimalSeparator?: string
  showSpaceForThousand?: boolean
  hideLabelIfPercentTooSmall?: boolean
  valuePercentUnderWhichHideLabel?: number
  /**
   * Camembert à une seule catégorie : complète la part manquante jusqu'à 100 % avec une
   * tranche neutre (rendu « jauge »). Pertinent pour un pourcentage, absurde pour un
   * compteur — réglable par widget dans « Style avancé ».
   * Défaut `true` = comportement historique : les widgets déjà en base n'ont pas la clé.
   */
  pieChartGaugeMode?: boolean
  // DATA
  valueData: string[]
  categoryData: string
  // BORDER
  showBorder?: boolean
  colorBorder?: string
  widthBorder?: number
  exteriorRadiusBorder?: number
  interiorRadiusBorder?: number
  borderActivation?: boolean
  // TICK
  disableTickLine?: boolean
  // TOOLTIP
  disableTooltipBorder?: boolean
  customTooltip?: boolean
  customTooltipBackground?: string
  customTooltipLabelColor?: string
  onlyShowPercentInLabel?: boolean
  // FOR SEMI PIECHART
  startAngle?: number
  endAngle?: number
  // LEGEND
  visibilityLegend?: boolean
  displayTypeLegendLabels?: "truncate" | "wrap" | "default"
  markerLegendHeigth?: number
  markerLegendWidth?: number
  legendPosition?: "top" | "bottom" | "left" | "right"
  fontSizeLegend?: number
  // SLICE - INITIAL
  initialSliceScale?: number
  initialSliceOpacity?: number
  // SLICE - CLICK
  scaleFactorOnSliceClick?: number
  opacityOnSliceClick?: number
  shiftRadiusOnSliceClick?: number
  // SLICE - HOVER
  scaleFactorOnSliceHover?: number
  opacityOnSliceHover?: number
  shiftRadiusOnSliceHover?: number
  // SLICE - DEFAULT
  defaultSliceScale?: number
  defaultSliceOpacity?: number
  defaultShiftRadius?: number
  colorSetTheme?: string[]
  colorMapper?: ColorMapperRow[]
  labelIsHorizontal?: boolean
  angleRelativeRotation?: number
  serieRadius?: number
  hideExternalSerieLabel?: boolean
  /**
   * Ordre EXPLICITE des parts (et donc de la légende). Les valeurs hors liste sont conservées et
   * placées en fin — voir `chartsLibrary/categoryOrder`. Absent = ordre de la donnée, inchangé.
   *
   * ⚠️ Pas de remplissage à 0 ici, contrairement à `BarChart` : une part nulle dans un camembert
   * n'a pas de surface, elle n'ajouterait qu'une entrée de légende sans contrepartie visuelle.
   */
  categoryOrder?: unknown[]
  externalLabelColor?: string
  externalValueWeight?: string
  externalValueColor?: string
  externalValueSize?: number
  externalLabelSize?: number
  externalLabelWeight?: string
  hideCenteredText?: boolean
  labelWeight?: string
  labelColor?: string
  labelSize?: string
  label?: string
  typeChart?: "pieChart" | "semiPieChart" | "doublePieChart" | "doubleSemiPieChart"
  withPurcentLabel?: boolean
  requestLoad?: any
  fieldsData?: any
  onSegmentClick?: (payload: { categoryValue: string; seriesIndex: number }) => void
}

const PieChart: React.FC<PieChartProps> = ({
  idChart,
  widgetTitle = "PieChart",
  height = "100%",
  width = "100%",
  dataChart = [],
  dataNames = [],
  innerRadius = 50,
  enableExportMenu = false,
  hideTitleInCenter = false,
  isCustomCenterValue,
  centeredValue = "default",
  customTextTitleCenter = "",
  colorTitleCenter = "#000",
  fontSizeTitleCenter = 15,
  fontWeightTitleCenter = "normal",
  displayAlignLabelType = "around",
  disableLabel = false,
  colorLabel = "#000",
  positionLabel = 0,
  fontSizeLabel = 15,
  customLabelText = "",
  typeOfExternalLabel,
  typeLabelText = "percent",
  numberDecimal = 0,
  decimalSeparator = ",",
  showSpaceForThousand = true,
  hideLabelIfPercentTooSmall = true,
  valuePercentUnderWhichHideLabel = 5,
  pieChartGaugeMode = true,
  valueData = [],
  categoryData,
  showBorder = false,
  colorBorder = "#fff",
  widthBorder = 1,
  exteriorRadiusBorder = 10,
  interiorRadiusBorder = 10,
  borderActivation = false,
  disableTickLine = false,
  disableTooltipBorder = false,
  customTooltip = false,
  customTooltipBackground = "#fff",
  customTooltipLabelColor = "#000",
  onlyShowPercentInLabel = false,
  startAngle = 0,
  endAngle = 360,
  visibilityLegend = false,
  displayTypeLegendLabels = "truncate",
  markerLegendHeigth = 20,
  markerLegendWidth = 20,
  legendPosition = "right",
  fontSizeLegend = 15,
  initialSliceScale = 1,
  initialSliceOpacity = 1,
  scaleFactorOnSliceClick = 1.1,
  opacityOnSliceClick = 0.5,
  shiftRadiusOnSliceClick = 0,
  scaleFactorOnSliceHover = 1.05,
  opacityOnSliceHover = 0.3,
  shiftRadiusOnSliceHover = 0,
  defaultSliceScale = 1,
  defaultSliceOpacity = 1,
  defaultShiftRadius = 0,
  colorSetTheme = ["#00b9c9", "#0b8a93", "#3b595b"],
  colorMapper = [],
  labelIsHorizontal = true,
  angleRelativeRotation = 90,
  serieRadius = 4,
  hideExternalSerieLabel = false,
  categoryOrder,
  externalLabelColor,
  externalValueWeight,
  externalValueColor,
  externalValueSize,
  externalLabelSize,
  externalLabelWeight,
  hideCenteredText,
  labelWeight,
  labelColor,
  labelSize = "1rem",
  label,
  typeChart = "pieChart",
  withPurcentLabel = false,
  requestLoad,
  fieldsData,
  onSegmentClick,
}) => {
  const ID = useRef(idChart || `pieChart${Math.random()}`).current
  const chartRef = useRef<am5percent.PieChart | null>(null)
  const rootRef = useRef<am5.Root | null>(null)
  const legendRef = useRef<am5.Legend | undefined>(undefined)
  const isMobile = useMediaQuery({ maxWidth: 750 })

  const { removeAllSeries } = useCleanChart()

  // Fills to 100% if single value: adds a "Reste" slice
  const ensureTwoSlices = (
    data: any[],
    valueData: string[],
    categoryData: string
  ): { data: any[]; mainPercent: number; showMainPercent: boolean } => {
    // Mode jauge désactivé pour ce widget → on n'affiche que la donnée réelle.
    if (!pieChartGaugeMode) {
      return { data, mainPercent: 0, showMainPercent: false }
    }
    if (!Array.isArray(data) || !data.length || !valueData?.length) {
      return { data, mainPercent: 0, showMainPercent: false }
    }
    const valueKey = valueData[0]

    // Multiple categories → no "Reste" slice
    if (data.length > 1) {
      return { data, mainPercent: 0, showMainPercent: false }
    }

    // Single value → mode jauge uniquement si la valeur est dans [0, 100] (ex. overallPercentage).
    // Si la valeur dépasse 100 c'est un COUNT brut — on n'applique pas le clamp et on n'ajoute
    // pas de tranche "Reste" (sinon Math.min(100, 504) → 100 et le tooltip affiche "(100)").
    const v = Number(data[0][valueKey])
    if (!isFinite(v) || v > 100) {
      return { data, mainPercent: 0, showMainPercent: false }
    }
    const valueClamped = Math.max(0, v)
    const rest = Math.max(0, 100 - valueClamped)

    const restSlice = {
      [valueKey]: rest,
      [categoryData]: "Reste",
      __isRestSlice: true,
    }

    return {
      data: [{ ...data[0], [valueKey]: valueClamped }, restSlice],
      mainPercent: valueClamped,
      showMainPercent: true,
    }
  }

  useEffect(() => {
    const root = am5.Root.new(ID)
    root._logo?.dispose()
    rootRef.current = root

    root.setThemes([am5themes_Animated.new(root)])

    // Number formatting
    configureNumberFormatting(root, {
      decimalSeparator,
      numberDecimal,
      showSpaceForThousand,
    })

    const chart = root.container.children.push(
      am5percent.PieChart.new(root, {
        layout: root.verticalLayout,
        innerRadius: am5.percent(
          ["doublePieChart", "doubleSemiPieChart"].includes(typeChart) ? 40 : innerRadius
        ),
        startAngle: startAngle - 90,
        endAngle: endAngle - 90,
      })
    )

    chartRef.current = chart

    // Export menu
    createExportMenu(root, {
      enableExportMenu: enableExportMenu ?? false,
      filePrefix: widgetTitle,
      dataSource: transformeData(requestLoad, fieldsData, dataChart),
    })

    // Legend
    legendRef.current = createLegend(chart, root, {
      visibilityLegend,
      legendPosition,
      displayTypeLegendLabels,
      fontSizeLegend,
      markerLegendWidth,
      markerLegendHeight: markerLegendHeigth,
      legendLabelText: "{category}",
    })

    return () => {
      root.dispose()
    }
  }, [])

  useEffect(() => {
    if (!chartRef.current || !rootRef.current) return

    const chart = chartRef.current
    const root = rootRef.current

    // Update chart properties
    chart.set("startAngle", startAngle - 90)
    chart.set("endAngle", endAngle - 90)
    chart.set(
      "innerRadius",
      am5.percent(["doublePieChart", "doubleSemiPieChart"].includes(typeChart) ? 40 : innerRadius)
    )

    // Ordonné AVANT `ensureTwoSlices` : la tranche de complément du mode jauge doit rester la
    // dernière, et l'ordre métier ne la concerne pas.
    const donneesOrdonnees = applyCategoryOrder(dataChart, categoryData, categoryOrder)

    const {
      data: completedData,
      mainPercent,
      showMainPercent,
    } = ensureTwoSlices(donneesOrdonnees, valueData, categoryData)

    // Clear existing series and legend
    chart.series.clear()
    clearLegends(chart)

    // Recreate legend
    legendRef.current = createLegend(chart, root, {
      visibilityLegend,
      legendPosition,
      displayTypeLegendLabels,
      fontSizeLegend,
      markerLegendWidth,
      markerLegendHeight: markerLegendHeigth,
    })

    // Force legend label text to ensure category is used via adapter
    if (legendRef.current) {
      legendRef.current.labels.template.adapters.add("text", (text, target) => {
        const dataItem = target.dataItem as any
        if (dataItem && dataItem.dataContext) {
          return dataItem.dataContext[categoryData] || text
        }
        return text
      })
    }

    // Create series based on chart type
    if (["doublePieChart", "doubleSemiPieChart"].includes(typeChart) && valueData.length >= 2) {
      // Inside series
      const seriesInside = createAndConfigureSeries(chart, root, valueData, categoryData, 0, true, {
        mainPercent,
        showMainPercent,
        startAngle: startAngle - 90,
        endAngle: endAngle - 90,
      })
      seriesInside.data.setAll(completedData)

      // Outside series
      const seriesOutside = createAndConfigureSeries(
        chart,
        root,
        valueData,
        categoryData,
        1,
        false,
        {
          mainPercent,
          showMainPercent,
          startAngle: startAngle - 90,
          endAngle: endAngle - 90,
        }
      )
      seriesOutside.data.setAll(completedData)
    } else {
      const series = createAndConfigureSeries(chart, root, valueData, categoryData, 0, false, {
        mainPercent,
        showMainPercent,
        startAngle: startAngle - 90,
        endAngle: endAngle - 90,
      })
      series.data.setAll(completedData)

      if (legendRef.current) {
        // La tranche de complément est masquée partout ailleurs (remplissage, label, tick,
        // tooltip, clic) : l'exclure aussi de la légende, sinon "Reste" y apparaît en clair.
        legendRef.current.data.setAll(
          series.dataItems.filter(di => !(di.dataContext as any)?.__isRestSlice)
        )
      }
    }
  })

  const createAndConfigureSeries = (
    chart: am5percent.PieChart,
    root: am5.Root,
    valueData: string[],
    categoryData: string,
    index: number,
    isInside: boolean,
    { mainPercent = 0, showMainPercent = false, startAngle = -90, endAngle = 270 } = {}
  ): am5percent.PieSeries => {
    const valueDataName = valueData[index] || valueData[0]

    const series = chart.series.push(
      am5percent.PieSeries.new(root, {
        valueField: valueDataName,
        categoryField: categoryData,
        alignLabels: typeOfExternalLabel !== "value",
        startAngle,
        endAngle,
      })
    )

    // Colors
    series.get("colors")?.set(
      "colors",
      colorSetTheme.map(c => am5.color(c))
    )

    // Couleur par valeur : une part de camembert EST une valeur, donc la regle porte sur sa
    // categorie. L'adapter ne rend une couleur que sur correspondance — sans regle, la palette
    // ci-dessus reste seule a decider et le rendu ne change pas.
    if (colorMapper.length) {
      const mapSliceColor = (fill: am5.Color | undefined, target: am5.Graphics) => {
        const dataItem = target.dataItem as am5.DataItem<am5percent.IPieSeriesDataItem> | undefined
        const mapped = resolveMappedColor(dataItem?.get("category"), colorMapper)
        return mapped ? am5.color(mapped) : fill
      }
      series.slices.template.adapters.add("fill", mapSliceColor)
    }

    // Slices configuration
    series.slices.template.setAll({
      cornerRadius: serieRadius,
      strokeOpacity: showBorder ? 1 : 0,
      strokeWidth: widthBorder,
      stroke: am5.color(colorBorder),
      scale: initialSliceScale,
      fillOpacity: initialSliceOpacity,
    })

    // borderActivation special styling
    if (borderActivation) {
      series.slices.template.setAll({
        stroke: am5.color("#FFFFFF"),
        strokeWidth: 1,
        strokeOpacity: 1,
      })
    }

    // Slice states
    const hoverState = series.slices.template.states.create("hover", {
      scale: borderActivation ? 1 : scaleFactorOnSliceHover,
      shiftRadius: borderActivation
        ? 0.05
        : shiftRadiusOnSliceHover > 0.5
        ? 0.2
        : shiftRadiusOnSliceHover,
    })

    series.slices.template.states.create("active", {
      scale: scaleFactorOnSliceClick,
      shiftRadius: shiftRadiusOnSliceClick > 0.5 ? 0.2 : shiftRadiusOnSliceClick,
    })

    // Drill-down: click on segment
    if (onSegmentClick) {
      series.slices.template.events.on("click", ev => {
        const dataContext = ev.target.dataItem?.dataContext as any
        if (dataContext?.__isRestSlice) return
        const categoryValue = dataContext?.[categoryData]
        if (categoryValue !== undefined) {
          onSegmentClick({ categoryValue: String(categoryValue), seriesIndex: index })
        }
      })
    }

    // Labels
    series.labels.template.setAll({
      text: customLabelText || "{category}",
      fill: am5.color(colorLabel),
      fontSize: fontSizeLabel,
      radius: positionLabel,
      visible: !(isMobile || hideExternalSerieLabel),
    })

    if (!labelIsHorizontal) {
      series.labels.template.set("rotation", angleRelativeRotation)
    }

    // External label custom formatting
    if (!isMobile && !hideExternalSerieLabel && typeOfExternalLabel) {
      series.labels.template.adapters.add("text", (text, target) => {
        const dataItem = target.dataItem as any
        if (!dataItem) return text

        const value = dataItem.get("value")
        const percent = dataItem.get("valuePercentTotal")
        const category = dataItem.get("category")

        if (value === undefined || percent === undefined) return text

        const typeValueSerieLabel = typeLabelText === "value" ? value : percent

        const formatedValue = formatNumberDisplayed(
          typeValueSerieLabel,
          numberDecimal,
          showSpaceForThousand,
          decimalSeparator
        )

        return getPieChartLabelFormat({
          typeOfExternalLabel,
          typeLabelText,
          externalLabelSize,
          externalLabelWeight,
          externalLabelColor,
          externalValueSize,
          externalValueWeight,
          externalValueColor,
          category,
          formatedValue,
        })
      })
    }

    // Ticks - disable when labels are hidden
    series.ticks.template.set("visible", !disableTickLine && !isMobile && !hideExternalSerieLabel)

    // Tooltips
    if (!disableTooltipBorder) {
      const numberFormat = getNumberFormat(numberDecimal, showSpaceForThousand)
      series.slices.template.setAll({
        tooltipText: `{category}: {valuePercentTotal.formatNumber('${numberFormat}')}% ({value})`,
      })
    }

    if (customTooltip) {
      series.slices.template.set(
        "tooltip",
        am5.Tooltip.new(root, {
          getFillFromSprite: false,
          labelText: "{category}: {valuePercentTotal}%",
        })
      )
      series.slices.template
        .get("tooltip")
        ?.get("background")
        ?.setAll({
          fill: am5.color(customTooltipBackground),
        })
      series.slices.template.get("tooltip")?.label.setAll({
        fill: am5.color(customTooltipLabelColor),
      })
    }

    // Center label (only for outer series)
    if (!isInside) {
      const titleOptions = {
        type: "sum",
        unit: "",
        chartType: "pie",
        showSpaceForThousand,
        numberOfDecimal: numberDecimal,
      }
      const defaultTitleText = getChartTextLabel(titleOptions)

      const valueTextWhenSingle = (() => {
        const formatted = formatNumberDisplayed(
          mainPercent ?? 0,
          numberDecimal,
          showSpaceForThousand,
          decimalSeparator
        )
        return `${formatted}%`
      })()

      if (!hideCenteredText && label) {
        series.children.push(
          am5.Label.new(root, {
            text: label,
            centerX: am5.p50,
            centerY: am5.p50,
            textAlign: "center",
            fontSize: labelSize,
            fill: am5.color(labelColor),
            fontWeight: labelWeight,
          })
        )
      }
    }

    // Double pie chart specific (inside series)
    if (["doublePieChart", "doubleSemiPieChart"].includes(typeChart) && isInside) {
      series.labels.template.set("visible", false)
      series.ticks.template.set("visible", false)
      series.set("radius", am5.percent(40))
    }

    // Legend settings
    if (["doublePieChart", "doubleSemiPieChart"].includes(typeChart)) {
      series.set("legendLabelText", `${dataNames[isInside ? 0 : 1]}: {category}`)
    }

    // Hide "Reste" slice styling
    series.slices.template.adapters.add("fillOpacity", (opacity, target) => {
      const dataContext = target.dataItem?.dataContext as any
      return dataContext?.__isRestSlice ? 0.12 : opacity
    })

    series.slices.template.adapters.add("strokeOpacity", (opacity, target) => {
      const dataContext = target.dataItem?.dataContext as any
      return dataContext?.__isRestSlice ? 0 : opacity
    })

    series.labels.template.adapters.add("text", (text, target) => {
      const dataContext = target.dataItem?.dataContext as any
      if (dataContext?.__isRestSlice) return ""

      if (hideLabelIfPercentTooSmall) {
        const percent = (target.dataItem as any)?.get("valuePercentTotal") || 0
        if (percent < valuePercentUnderWhichHideLabel) return ""
      }

      return text
    })

    series.ticks.template.adapters.add("visible", (visible, target) => {
      const dataContext = target.dataItem?.dataContext as any
      return dataContext?.__isRestSlice ? false : visible
    })

    series.slices.template.adapters.add("interactive", (interactive, target) => {
      const dataContext = target.dataItem?.dataContext as any
      return dataContext?.__isRestSlice ? false : interactive
    })

    series.slices.template.adapters.add("tooltipText", (text, target) => {
      const dataContext = target.dataItem?.dataContext as any
      return dataContext?.__isRestSlice ? "" : text
    })

    // withPurcentLabel - Add percentage labels inside slices
    if (withPurcentLabel) {
      series.bullets.push((root, series, dataItem) => {
        const percent = (dataItem as any).get("valuePercentTotal") || 0

        // Only create bullet for slices < 50%
        if (round(percent) >= 50) {
          return undefined
        }

        return am5.Bullet.new(root, {
          locationX: 0.5,
          locationY: 0.5,
          sprite: am5.Label.new(root, {
            text: `${round(percent)}%`,
            fontSize: 17.5,
            fontWeight: "bold",
            fill: am5.color("#FFF"),
            centerX: am5.p50,
            centerY: am5.p50,
          }),
        })
      })
    }

    return series
  }

  return <div id={ID} style={{ width, height }} />
}

export default PieChart
